import React, { useState } from 'react';
import { saveCustomProgram } from '../data/programs';

const LIFT_VARIANTS = {
  bench: ['Bench Press', 'Close Grip Bench', 'Pin/Floor Press', 'Dips'],
  squat: ['Squat', 'Front Squat'],
  deadlift: ['Deadlift (primary stance)', 'Deadlift (opposite stance)', 'RDL', 'Rack Pull'],
};

const NAME_MODIFIERS = ['Paused', 'Beltless', 'Wide Stance'];
const PROTOCOL_MODIFIERS = ['EMOM', 'Tempo'];

const RM_OPTIONS = ['3RM', '5RM', '8RM', '10RM', '12RM', '15RM'];
const PCT_OPTIONS = [50, 55, 60, 65, 70, 75, 80, 85, 90, 95];

const PERF_TYPES = [
  { id: 'percentage', label: 'Percentage' },
  { id: 'rm', label: 'RM test' },
  { id: 'amap', label: 'AMAP' },
  { id: 'maxTest', label: '1RM test' },
  { id: 'same', label: 'Same weight' },
  { id: 'heavier', label: 'Heavier' },
  { id: 'rpe', label: 'RPE target' },
  { id: 'accessory', label: 'Accessory' },
];

const liftColors = {
  bench: 'var(--bench-color)', squat: 'var(--squat-color)', deadlift: 'var(--deadlift-color)',
};

function defaultExForm(lift) {
  return {
    variant: LIFT_VARIANTS[lift]?.[0] || 'Bench Press',
    customName: '',
    isCustomVariant: false,
    performanceType: 'percentage',
    percentage: 80,
    rmType: '10RM',
    rpeTarget: 8,
    sets: 3,
    reps: '5',
    modifiers: [],
    notes: '',
  };
}

function buildExerciseName(form) {
  const base = form.isCustomVariant ? form.customName : form.variant;
  const nameMods = form.modifiers.filter(m => NAME_MODIFIERS.includes(m));
  if (nameMods.length > 0) return `${base} (${nameMods.map(m => m.toLowerCase()).join(', ')})`;
  return base;
}

function formToExercise(form) {
  const name = buildExerciseName(form);
  const ex = { name, sets: parseInt(form.sets) || 1, reps: form.reps || '5' };

  const protocols = form.modifiers.filter(m => PROTOCOL_MODIFIERS.includes(m));
  const noteParts = [];
  if (protocols.length > 0) noteParts.push(protocols.join(', '));
  if (form.notes) noteParts.push(form.notes);
  if (noteParts.length > 0) ex.notes = noteParts.join('. ');

  switch (form.performanceType) {
    case 'percentage':
      ex.percentage = (parseInt(form.percentage) || 80) / 100;
      break;
    case 'rm':
      ex.weight = form.rmType || '10RM';
      break;
    case 'amap':
      ex.percentage = (parseInt(form.percentage) || 80) / 100;
      ex.reps = 'AMAP';
      ex.isAMAP = true;
      break;
    case 'maxTest':
      ex.weight = 'New 1RM'; ex.reps = '1'; ex.sets = 1; ex.isMaxTest = true;
      break;
    case 'same':
      ex.weight = 'same';
      break;
    case 'heavier':
      ex.weight = 'Heavier';
      break;
    case 'rpe':
      ex.isRPE = true; ex.rpeTarget = parseFloat(form.rpeTarget) || 8;
      break;
    case 'accessory':
      ex.isAccessory = true;
      break;
    default: break;
  }
  return ex;
}

function buildProgram(data) {
  const id = `custom_${data.lift}_${Date.now()}`;
  const displayName = data.name.trim() ||
    `Custom ${data.lift.charAt(0).toUpperCase() + data.lift.slice(1)} ${data.frequency}x/week`;

  const weeks = data.weeks.map((week, wi) => {
    const weekObj = {
      weekNum: wi + 1,
      days: week.days.map((dayExs, di) => ({
        dayNum: di + 1,
        exercises: dayExs.map(formToExercise),
      })),
    };
    if (data.hasMaxTest && wi === data.weeks.length - 1) weekObj.isMaxTest = true;
    return weekObj;
  });

  return { id, lift: data.lift, frequency: `${data.frequency}x`, level: 'Custom', displayName, isCustom: true, weeks };
}

function exerciseSummary(form) {
  const name = buildExerciseName(form);
  let detail = '';
  switch (form.performanceType) {
    case 'percentage': detail = `${form.percentage}% · ${form.sets}×${form.reps}`; break;
    case 'rm': detail = `${form.rmType} · ${form.sets}×${form.reps}`; break;
    case 'amap': detail = `${form.percentage}% · ${form.sets}×AMAP`; break;
    case 'maxTest': detail = '1×1RM test'; break;
    case 'same': detail = `same · ${form.sets}×${form.reps}`; break;
    case 'heavier': detail = `heavier · ${form.sets}×${form.reps}`; break;
    case 'rpe': detail = `RPE ${form.rpeTarget} · ${form.sets}×${form.reps}`; break;
    case 'accessory': detail = `${form.sets}×${form.reps}`; break;
    default: detail = `${form.sets}×${form.reps}`;
  }
  return { name, detail };
}

// =====================================================
// COMPONENT
// =====================================================

export default function ProgramBuilder({ onSave, onCancel }) {
  const [screen, setScreen] = useState('setup');
  const [name, setName] = useState('');
  const [lift, setLift] = useState('bench');
  const [frequency, setFrequency] = useState(2);
  const [cycleLength, setCycleLength] = useState(4);
  const [hasMaxTest, setHasMaxTest] = useState(true);
  const [weeks, setWeeks] = useState([]);
  const [selWeek, setSelWeek] = useState(0);
  const [selDay, setSelDay] = useState(0);
  const [editIdx, setEditIdx] = useState(null); // null = adding, number = editing
  const [exForm, setExForm] = useState(() => defaultExForm('bench'));

  const color = liftColors[lift] || 'var(--accent)';

  // === SETUP → EDITOR transition ===
  const handleSetupDone = () => {
    const w = Array.from({ length: cycleLength }, (_, wi) => ({
      days: Array.from({ length: frequency }, () => []),
    }));
    // Pre-populate max test week
    if (hasMaxTest && w.length > 0) {
      const mainLift = LIFT_VARIANTS[lift]?.[0] || 'Bench Press';
      w[w.length - 1].days[0] = [{
        ...defaultExForm(lift),
        variant: mainLift,
        performanceType: 'maxTest',
        sets: 1,
        reps: '1',
      }];
    }
    setWeeks(w);
    setSelWeek(0);
    setSelDay(0);
    setScreen('editor');
  };

  // === EXERCISE CRUD ===
  const currentExercises = weeks[selWeek]?.days?.[selDay] || [];

  const updateCurrentDay = (newExs) => {
    setWeeks(prev => {
      const next = prev.map((w, wi) => wi === selWeek ? {
        ...w,
        days: w.days.map((d, di) => di === selDay ? newExs : d),
      } : w);
      return next;
    });
  };

  const handleAddExercise = () => {
    setEditIdx(null);
    setExForm(defaultExForm(lift));
    setScreen('exercise');
  };

  const handleEditExercise = (idx) => {
    setEditIdx(idx);
    setExForm({ ...currentExercises[idx] });
    setScreen('exercise');
  };

  const handleSaveExercise = () => {
    const ex = { ...exForm };
    if (editIdx !== null) {
      const updated = [...currentExercises];
      updated[editIdx] = ex;
      updateCurrentDay(updated);
    } else {
      updateCurrentDay([...currentExercises, ex]);
    }
    setScreen('editor');
  };

  const handleDeleteExercise = (idx) => {
    updateCurrentDay(currentExercises.filter((_, i) => i !== idx));
  };

  const handleMoveExercise = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= currentExercises.length) return;
    const updated = [...currentExercises];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updateCurrentDay(updated);
  };

  const handleCopyWeek = (fromWeek) => {
    setWeeks(prev => prev.map((w, wi) => wi === selWeek ? {
      days: prev[fromWeek].days.map(d => d.map(ex => ({ ...ex, modifiers: [...ex.modifiers] }))),
    } : w));
  };

  // === SAVE PROGRAM ===
  const handleSaveProgram = () => {
    const data = { name, lift, frequency, cycleLength, hasMaxTest, weeks };
    const program = buildProgram(data);
    saveCustomProgram(program);
    onSave(program);
  };

  // Validation
  const hasExercises = weeks.every(w => w.days.every(d => d.length > 0));
  const totalExercises = weeks.reduce((sum, w) => sum + w.days.reduce((s, d) => s + d.length, 0), 0);

  // =====================================================
  // SETUP SCREEN
  // =====================================================
  if (screen === 'setup') {
    return (
      <div>
        <div className="session-header">
          <h1>Build Custom Program</h1>
          <p className="sub">Design your own training program</p>
        </div>

        <div className="card" style={{ padding: 14 }}>
          <div style={labelStyle}>Program name</div>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder={`Custom ${lift.charAt(0).toUpperCase() + lift.slice(1)} ${frequency}x/week`}
            style={{ marginBottom: 16 }} />

          <div style={labelStyle}>Lift</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {['bench', 'squat', 'deadlift'].map(l => (
              <button key={l} onClick={() => setLift(l)} style={pillStyle(lift === l, liftColors[l])}>
                {l.charAt(0).toUpperCase() + l.slice(1)}
              </button>
            ))}
          </div>

          <div style={labelStyle}>Frequency</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {[1, 2, 3].map(f => (
              <button key={f} onClick={() => setFrequency(f)} style={pillStyle(frequency === f)}>
                {f}x / week
              </button>
            ))}
          </div>

          <div style={labelStyle}>Cycle length</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <button style={stepperBtnStyle} onClick={() => setCycleLength(Math.max(1, cycleLength - 1))}>−</button>
            <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 22, fontWeight: 600, minWidth: 30, textAlign: 'center' }}>
              {cycleLength}
            </span>
            <button style={stepperBtnStyle} onClick={() => setCycleLength(Math.min(12, cycleLength + 1))}>+</button>
            <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>weeks</span>
          </div>

          <div style={labelStyle}>Max test on final week?</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button onClick={() => setHasMaxTest(true)} style={pillStyle(hasMaxTest)}>Yes — final week</button>
            <button onClick={() => setHasMaxTest(false)} style={pillStyle(!hasMaxTest)}>No</button>
          </div>
        </div>

        <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={handleSetupDone}>
          Next — Build Weeks
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={onCancel}>Cancel</button>
      </div>
    );
  }

  // =====================================================
  // EDITOR SCREEN
  // =====================================================
  if (screen === 'editor') {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button className="btn btn-secondary btn-small" style={{ width: 'auto' }}
            onClick={() => setScreen('setup')}>←</button>
          <div>
            <h1 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 18, fontWeight: 600 }}>
              {name || `Custom ${lift.charAt(0).toUpperCase() + lift.slice(1)}`}
            </h1>
            <p className="sub">{frequency}x/week · {cycleLength} weeks{hasMaxTest ? ' · max test W' + cycleLength : ''}</p>
          </div>
        </div>

        {/* Week tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {weeks.map((_, wi) => (
            <button key={wi} onClick={() => { setSelWeek(wi); setSelDay(0); }}
              style={{
                ...pillStyle(selWeek === wi, hasMaxTest && wi === weeks.length - 1 ? 'var(--max-test-border)' : undefined),
                minWidth: 44, flex: 'none',
              }}>
              W{wi + 1}{hasMaxTest && wi === weeks.length - 1 ? ' ★' : ''}
            </button>
          ))}
        </div>

        {/* Day tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {Array.from({ length: frequency }, (_, di) => (
            <button key={di} onClick={() => setSelDay(di)}
              style={pillStyle(selDay === di, color)}>
              Day {di + 1}
              {(weeks[selWeek]?.days?.[di]?.length || 0) > 0 &&
                <span style={{ marginLeft: 4, opacity: 0.6 }}>({weeks[selWeek]?.days?.[di]?.length})</span>
              }
            </button>
          ))}
        </div>

        {/* Copy from week */}
        {selWeek > 0 && (
          <div style={{ marginBottom: 12 }}>
            <button className="btn btn-secondary btn-small" style={{ width: 'auto', fontSize: 11 }}
              onClick={() => handleCopyWeek(selWeek - 1)}>
              Copy from W{selWeek} → W{selWeek + 1}
            </button>
          </div>
        )}

        {/* Exercise list */}
        <div className="card" style={{ padding: currentExercises.length > 0 ? '8px 14px' : 14 }}>
          {currentExercises.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 8 }}>
              No exercises yet — tap below to add
            </div>
          ) : (
            currentExercises.map((ex, i) => {
              const { name: exName, detail } = exerciseSummary(ex);
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', padding: '10px 0',
                  borderBottom: i < currentExercises.length - 1 ? '1px solid var(--border)' : 'none',
                  gap: 8,
                }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', background: '#222',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, color: '#666', flexShrink: 0,
                  }}>{i + 1}</div>
                  <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => handleEditExercise(i)}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {exName}
                      {ex.performanceType === 'amap' && <span style={badgeStyle('#cc8800')}>AMAP</span>}
                      {ex.performanceType === 'maxTest' && <span style={badgeStyle('var(--max-test-border)')}>MAX</span>}
                      {ex.performanceType === 'accessory' && <span style={badgeStyle('#888')}>ACC</span>}
                      {ex.modifiers.includes('EMOM') && <span style={badgeStyle('#5aadff')}>EMOM</span>}
                      {ex.performanceType === 'rpe' && <span style={badgeStyle('#c77dff')}>RPE</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{detail}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button style={iconBtnStyle} onClick={() => handleMoveExercise(i, -1)} disabled={i === 0}>↑</button>
                    <button style={iconBtnStyle} onClick={() => handleMoveExercise(i, 1)} disabled={i === currentExercises.length - 1}>↓</button>
                  </div>
                  <button style={{ ...iconBtnStyle, color: 'var(--danger)' }} onClick={() => handleDeleteExercise(i)}>✕</button>
                </div>
              );
            })
          )}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: 10, border: '1px dashed #333', borderRadius: 8, color: '#555',
            fontSize: 13, cursor: 'pointer', marginTop: currentExercises.length > 0 ? 8 : 0,
          }} onClick={handleAddExercise}>+ Add exercise</div>
        </div>

        {/* Week summary */}
        <div className="card" style={{ padding: 14, marginTop: 8 }}>
          <div style={labelStyle}>Week {selWeek + 1} summary</div>
          {Array.from({ length: frequency }, (_, di) => {
            const dayExs = weeks[selWeek]?.days?.[di] || [];
            return (
              <div key={di} style={{ marginBottom: di < frequency - 1 ? 8 : 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color, fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', marginBottom: 2 }}>
                  Day {di + 1}
                </div>
                {dayExs.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Empty</div>
                ) : (
                  dayExs.map((ex, ei) => {
                    const { name: n, detail: d } = exerciseSummary(ex);
                    return (
                      <div key={ei} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-dim)', padding: '2px 0' }}>
                        <span>{n}</span><span>{d}</span>
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}
        </div>

        <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setScreen('preview')}
          disabled={!hasExercises}>
          {hasExercises ? 'Preview Program' : 'Add exercises to all days first'}
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={onCancel}>Cancel</button>
      </div>
    );
  }

  // =====================================================
  // ADD/EDIT EXERCISE SCREEN
  // =====================================================
  if (screen === 'exercise') {
    const variants = LIFT_VARIANTS[lift] || [];
    const showIntensity = ['percentage', 'amap'].includes(exForm.performanceType);
    const showRM = exForm.performanceType === 'rm';
    const showRPE = exForm.performanceType === 'rpe';
    const showSetsReps = !['maxTest'].includes(exForm.performanceType);
    const isMaxTest = exForm.performanceType === 'maxTest';

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <button className="btn btn-secondary btn-small" style={{ width: 'auto' }}
            onClick={() => setScreen('editor')}>←</button>
          <h1 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 18, fontWeight: 600 }}>
            {editIdx !== null ? 'Edit Exercise' : 'Add Exercise'}
          </h1>
        </div>

        {/* Variant selection */}
        <div className="card" style={{ padding: 14, marginBottom: 8 }}>
          <div style={labelStyle}>Lift variant</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
            {variants.map(v => (
              <button key={v} onClick={() => setExForm(f => ({ ...f, variant: v, isCustomVariant: false }))}
                style={variantStyle(!exForm.isCustomVariant && exForm.variant === v)}>
                {v}
              </button>
            ))}
            <button onClick={() => setExForm(f => ({ ...f, isCustomVariant: true }))}
              style={variantStyle(exForm.isCustomVariant)}>
              Accessory ✎
            </button>
          </div>
          {exForm.isCustomVariant && (
            <input type="text" value={exForm.customName}
              onChange={e => setExForm(f => ({ ...f, customName: e.target.value }))}
              placeholder="Exercise name (e.g. Curls, Rows)" style={{ marginTop: 4 }} />
          )}
        </div>

        {/* Performance type */}
        <div className="card" style={{ padding: 14, marginBottom: 8 }}>
          <div style={labelStyle}>Performance type</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {PERF_TYPES.map(pt => (
              <button key={pt.id} onClick={() => {
                const updates = { performanceType: pt.id };
                if (pt.id === 'amap') updates.reps = 'AMAP';
                else if (pt.id === 'maxTest') { updates.sets = 1; updates.reps = '1'; }
                else if (exForm.reps === 'AMAP' && pt.id !== 'amap') updates.reps = '5';
                setExForm(f => ({ ...f, ...updates }));
              }}
                style={variantStyle(exForm.performanceType === pt.id)}>
                {pt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Intensity */}
        {showIntensity && (
          <div className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={labelStyle}>Percentage of training max</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PCT_OPTIONS.map(p => (
                <button key={p} onClick={() => setExForm(f => ({ ...f, percentage: p }))}
                  style={{
                    ...pillStyle(exForm.percentage === p, color),
                    minWidth: 44, textAlign: 'center',
                  }}>{p}%</button>
              ))}
            </div>
          </div>
        )}

        {showRM && (
          <div className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={labelStyle}>RM type</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {RM_OPTIONS.map(rm => (
                <button key={rm} onClick={() => setExForm(f => ({ ...f, rmType: rm }))}
                  style={pillStyle(exForm.rmType === rm, color)}>{rm}</button>
              ))}
            </div>
          </div>
        )}

        {showRPE && (
          <div className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={labelStyle}>RPE target</div>
            <div className="rpe-selector">
              {[6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10].map(v => (
                <button key={v} className={`rpe-btn ${exForm.rpeTarget === v ? 'selected' : ''}`}
                  onClick={() => setExForm(f => ({ ...f, rpeTarget: v }))}>{v}</button>
              ))}
            </div>
          </div>
        )}

        {/* Sets & Reps */}
        {showSetsReps && (
          <div className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>Sets</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button style={stepperBtnStyle} onClick={() => setExForm(f => ({ ...f, sets: Math.max(1, f.sets - 1) }))}>−</button>
                  <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 20, fontWeight: 600, minWidth: 24, textAlign: 'center' }}>
                    {exForm.sets}
                  </span>
                  <button style={stepperBtnStyle} onClick={() => setExForm(f => ({ ...f, sets: Math.min(20, f.sets + 1) }))}>+</button>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>Reps</div>
                <input type="text" value={exForm.reps}
                  onChange={e => setExForm(f => ({ ...f, reps: e.target.value }))}
                  placeholder="e.g. 5, 8-10, AMAP"
                  style={{ textAlign: 'center', fontSize: 16, fontWeight: 600, padding: 8 }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
              {['AMAP', 'AMAP-2'].map(r => (
                <button key={r} onClick={() => setExForm(f => ({ ...f, reps: r }))}
                  style={{ ...pillStyle(exForm.reps === r, '#cc8800'), fontSize: 11 }}>{r}</button>
              ))}
              {['1', '3', '5', '8', '10'].map(r => (
                <button key={r} onClick={() => setExForm(f => ({ ...f, reps: r }))}
                  style={{ ...pillStyle(exForm.reps === r), fontSize: 11 }}>{r}</button>
              ))}
            </div>
          </div>
        )}

        {isMaxTest && (
          <div className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--text-dim)', textAlign: 'center' }}>
              Work up to a new 1RM · 1 × 1
            </div>
          </div>
        )}

        {/* Modifiers */}
        <div className="card" style={{ padding: 14, marginBottom: 8 }}>
          <div style={labelStyle}>Execution modifiers</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[...NAME_MODIFIERS, ...PROTOCOL_MODIFIERS].map(mod => {
              const active = exForm.modifiers.includes(mod);
              return (
                <button key={mod} onClick={() => {
                  setExForm(f => ({
                    ...f,
                    modifiers: active ? f.modifiers.filter(m => m !== mod) : [...f.modifiers, mod],
                  }));
                }} style={pillStyle(active, active ? '#c77dff' : undefined)}>
                  {mod}{active && ' ✓'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div className="card" style={{ padding: 14, marginBottom: 8 }}>
          <div style={labelStyle}>Notes (optional)</div>
          <input type="text" value={exForm.notes}
            onChange={e => setExForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="e.g. 10 min EMOM, stop at form breakdown" />
        </div>

        {/* Preview */}
        {(exForm.variant || exForm.customName) && (
          <div style={{
            background: 'var(--bg-elevated)', borderRadius: 8, padding: '8px 14px', marginBottom: 8,
            fontSize: 13, color: 'var(--text-dim)',
          }}>
            Preview: <strong style={{ color: 'var(--text)' }}>{exerciseSummary(exForm).name}</strong>
            {' '} — {exerciseSummary(exForm).detail}
          </div>
        )}

        <button className="btn btn-primary" onClick={handleSaveExercise}
          disabled={!exForm.variant && !exForm.customName}>
          {editIdx !== null ? 'Update Exercise' : `Add to W${selWeek + 1} D${selDay + 1}`}
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => setScreen('editor')}>Cancel</button>
      </div>
    );
  }

  // =====================================================
  // PREVIEW SCREEN
  // =====================================================
  if (screen === 'preview') {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <button className="btn btn-secondary btn-small" style={{ width: 'auto' }}
            onClick={() => setScreen('editor')}>←</button>
          <div>
            <h1 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 18, fontWeight: 600 }}>
              {name || `Custom ${lift.charAt(0).toUpperCase() + lift.slice(1)} ${frequency}x/week`}
            </h1>
            <p className="sub">{cycleLength} weeks · {frequency}x/week · {totalExercises} exercises</p>
          </div>
        </div>

        {weeks.map((week, wi) => {
          const isMaxWeek = hasMaxTest && wi === weeks.length - 1;
          return (
            <div key={wi} className="card" style={{
              padding: '10px 14px', marginBottom: 8,
              borderLeft: isMaxWeek ? '2px solid var(--max-test-border)' : '2px solid var(--border)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{
                  fontFamily: 'Barlow Condensed, sans-serif', fontSize: 14, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: isMaxWeek ? 'var(--max-test-border)' : 'var(--text-dim)',
                }}>
                  Week {wi + 1}{isMaxWeek ? ' — Max Test' : ''}
                </span>
                <span style={{ fontSize: 11, color: '#555' }}>{frequency} day{frequency !== 1 ? 's' : ''}</span>
              </div>
              {week.days.map((dayExs, di) => (
                <div key={di} style={{ marginBottom: di < frequency - 1 ? 8 : 0 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 600, color, fontFamily: 'Barlow Condensed, sans-serif',
                    textTransform: 'uppercase', marginBottom: 2,
                  }}>Day {di + 1}</div>
                  {dayExs.map((ex, ei) => {
                    const { name: n, detail: d } = exerciseSummary(ex);
                    return (
                      <div key={ei} style={{
                        display: 'flex', justifyContent: 'space-between', padding: '3px 0',
                        fontSize: 12, color: 'var(--text-dim)',
                      }}>
                        <span>
                          {n}
                          {ex.performanceType === 'amap' && <span style={{ ...badgeStyle('#cc8800'), fontSize: 9 }}>AMAP</span>}
                          {ex.performanceType === 'maxTest' && <span style={{ ...badgeStyle('var(--max-test-border)'), fontSize: 9 }}>MAX</span>}
                        </span>
                        <span style={{ color: '#666' }}>{d}</span>
                      </div>
                    );
                  })}
                  {dayExs.length === 0 && (
                    <div style={{ fontSize: 11, color: 'var(--danger)' }}>No exercises!</div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={handleSaveProgram}
          disabled={!hasExercises}>
          Save & Activate
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => setScreen('editor')}>
          Edit
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={onCancel}>Cancel</button>
      </div>
    );
  }

  return null;
}

// =====================================================
// SHARED STYLES
// =====================================================

const labelStyle = {
  fontSize: 12, color: 'var(--text-dim)', fontFamily: 'Barlow Condensed, sans-serif',
  textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8,
};

function pillStyle(active, activeColor) {
  const c = activeColor || 'var(--accent)';
  return {
    flex: 1, padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${active ? c : 'var(--border)'}`,
    background: active ? `${c}15` : 'none',
    color: active ? c : 'var(--text-dim)',
    fontFamily: 'Barlow Condensed, sans-serif', fontSize: 13, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

function variantStyle(active) {
  return {
    padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    background: active ? 'rgba(74,158,255,0.06)' : 'var(--bg)',
    color: active ? 'var(--text)' : 'var(--text-muted)',
    fontSize: 12, cursor: 'pointer', textAlign: 'center',
  };
}

const stepperBtnStyle = {
  width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-elevated)', color: 'var(--text-dim)', fontSize: 16,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const iconBtnStyle = {
  background: 'none', border: 'none', color: 'var(--text-muted)',
  fontSize: 12, cursor: 'pointer', padding: '2px 4px',
};

function badgeStyle(color) {
  return {
    fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
    marginLeft: 4, background: `${color}20`, color,
  };
}
